import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEC, severityColor } from './SecurityTheme';
import { getAllSecurityAlerts, getIPGeolocation, SecurityAlertItem } from '../../services/api';
import {
  ChevronLeft,
  Globe,
  MapPin,
  Loader2,
  RefreshCw,
} from 'lucide-react';

interface GeoEntry {
  ip: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  lat: number;
  lon: number;
  isp: string;
  org: string;
  alertSeverity: string;
  clientName: string;
  alertTitle: string;
  threatScore?: number;
  threatType?: string;
}

export default function ThreatMap() {
  const [geoEntries, setGeoEntries] = useState<GeoEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [sortCol, setSortCol] = useState<'country' | 'city' | 'severity' | 'threatScore'>('severity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const alertsResp = await getAllSecurityAlerts({ limit: 100 });
      if (!alertsResp.success) {
        setError('Failed to load alerts');
        return;
      }

      // Extract unique IPs from alert affected users patterns or use alerts directly
      // For now, create geo entries from alert data
      const entries: GeoEntry[] = [];

      // We'll look up IPs from alerts that have them in their summary/title
      const ipRegex = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
      const seenIps = new Set<string>();
      const ipAlertMap: { ip: string; alert: SecurityAlertItem }[] = [];

      for (const alert of alertsResp.alerts) {
        const textToSearch = `${alert.title} ${alert.summary}`;
        const matches = textToSearch.match(ipRegex) || [];
        for (const ip of matches) {
          if (!seenIps.has(ip) && !ip.startsWith('10.') && !ip.startsWith('192.168.') && !ip.startsWith('172.')) {
            seenIps.add(ip);
            ipAlertMap.push({ ip, alert });
          }
        }
      }

      // Look up geolocation for discovered IPs (limit to first 20 to avoid rate limiting)
      if (ipAlertMap.length > 0) {
        setLookingUp(true);
        const lookups = ipAlertMap.slice(0, 20);
        const results = await Promise.allSettled(
          lookups.map(({ ip }) => getIPGeolocation(ip))
        );

        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          if (result.status === 'fulfilled' && result.value.success && result.value.geolocation) {
            const geo = result.value.geolocation;
            const { alert } = lookups[i];
            entries.push({
              ip: geo.ip,
              city: geo.city || 'Unknown',
              state: geo.state || '',
              country: geo.country || 'Unknown',
              countryCode: geo.countryCode || '',
              lat: geo.lat || 0,
              lon: geo.lon || 0,
              isp: geo.isp || '',
              org: geo.org || '',
              alertSeverity: alert.severity,
              clientName: alert.clientName,
              alertTitle: alert.title,
              threatScore: result.value.threatIntel?.abuseScore,
              threatType: result.value.threatIntel?.threatType,
            });
          }
        }
        setLookingUp(false);
      }

      setGeoEntries(entries);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load threat map data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort entries
  const sortedEntries = [...geoEntries].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortCol) {
      case 'country': return dir * a.country.localeCompare(b.country);
      case 'city': return dir * a.city.localeCompare(b.city);
      case 'threatScore': return dir * ((a.threatScore || 0) - (b.threatScore || 0));
      case 'severity': {
        const sevOrder = { critical: 3, warning: 2, info: 1 };
        const av = sevOrder[a.alertSeverity as keyof typeof sevOrder] || 0;
        const bv = sevOrder[b.alertSeverity as keyof typeof sevOrder] || 0;
        return dir * (av - bv);
      }
      default: return 0;
    }
  });

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const sortArrow = (col: typeof sortCol) => {
    if (sortCol !== col) return null;
    return <span className="ml-1">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  return (
    <div className="min-h-screen" style={{ background: SEC.bg }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-6 py-3 flex items-center justify-between"
        style={{
          background: SEC.header,
          borderBottom: `1px solid ${SEC.border}`,
          boxShadow: `0 1px 8px rgba(0,0,0,0.4)`,
        }}
      >
        <div className="flex items-center gap-3">
          <Link to="/security" className="p-1 rounded hover:bg-white/5 transition-colors">
            <ChevronLeft className="h-5 w-5" style={{ color: SEC.t2 }} />
          </Link>
          <Globe className="h-5 w-5" style={{ color: SEC.cyan }} />
          <h1
            className="text-sm font-bold tracking-[0.2em] uppercase"
            style={{ color: SEC.t1 }}
          >
            Global Threat Map
          </h1>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
          style={{ color: SEC.cyan, border: `1px solid ${SEC.cyanDim}` }}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div
            className="px-4 py-3 rounded text-sm"
            style={{ background: `${SEC.red}10`, color: SEC.red, border: `1px solid ${SEC.red}30` }}
          >
            {error}
            <button onClick={fetchData} className="ml-3 underline text-xs">Retry</button>
          </div>
        )}
        {/* Map Placeholder */}
        <div
          className="rounded-lg overflow-hidden relative"
          style={{
            background: SEC.panel,
            border: `1px solid ${SEC.borderBr}`,
            minHeight: '400px',
          }}
        >
          <div
            className="flex items-center gap-2 px-4 py-3"
            style={{ borderBottom: `1px solid ${SEC.border}` }}
          >
            <Globe className="h-4 w-4" style={{ color: SEC.cyan }} />
            <h3
              className="text-xs font-semibold tracking-[0.15em] uppercase"
              style={{ color: SEC.t1 }}
            >
              Geographic Threat Visualization
            </h3>
          </div>
          <div className="flex flex-col items-center justify-center py-20">
            {/* ASCII world map placeholder */}
            <div
              className="font-mono text-[8px] leading-tight mb-6 opacity-30 select-none"
              style={{ color: SEC.cyan }}
            >
              <pre>{`
        . . . . . . . . . . . . . . . . . . . . . . . .
      . . @@@@@ . . . @@@@@@@@@@@@@@@@@ . . . . . . . . .
    . . @@@@@@@@ . . @@@@@@@@@@@@@@@@@@@@ . . @@@@@ . . . .
   . . @@@@@@@@@ . . @@@@@@@@@@@@@@@@@@@@@ . @@@@@@@ . . . .
   . . @@@@@@@@ . . . @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ . . . .
    . . @@@@@ . . . . . @@@@@@@@@@@@@@@@@@@@@@@@@@@ . . . .
      . . . . . . . . . @@@@@@@@@@@@@@@@@@@@@@@@@ . . . . .
        . . . . . . . . . @@@@@@@@ . @@@@@@@@@@ . . . . .
          . . . . . . . . . @@@ . . . . @@@@@ . . . . .
            . . . . . . . . . . . . . . . @@ . . . . .
              . . . . . . . . . . . . . . . . . . .
                . . . . . . . . . . . @@@@@@ . .
                  . . . . . . . . . . @@@@@@@@ .
                    . . . . . . . . . . @@@@@
                      . . . . . . . . . . .
              `}</pre>
            </div>
            {geoEntries.length > 0 ? (
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: SEC.t2 }}>
                  {geoEntries.length} threat location{geoEntries.length !== 1 ? 's' : ''} detected
                </p>
                <p className="text-xs mt-1" style={{ color: SEC.t3 }}>
                  Interactive Leaflet map available after <span className="font-mono" style={{ color: SEC.cyan }}>npm install leaflet react-leaflet</span>
                </p>
              </div>
            ) : loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: SEC.cyan }} />
                <span className="text-sm" style={{ color: SEC.t3 }}>Scanning for threat locations...</span>
              </div>
            ) : (
              <div className="text-center">
                <MapPin className="h-8 w-8 mx-auto mb-2" style={{ color: SEC.t3 }} />
                <p className="text-sm" style={{ color: SEC.t2 }}>No geographic threat data available</p>
                <p className="text-xs mt-1" style={{ color: SEC.t3 }}>
                  Run a forensic collection to detect IP-based threats
                </p>
              </div>
            )}
          </div>

          {/* Location dots overlay */}
          {geoEntries.length > 0 && (
            <div className="absolute inset-0 pointer-events-none" style={{ top: '48px' }}>
              {geoEntries.slice(0, 15).map((entry, i) => {
                // Simple lat/lon to x/y mapping for the placeholder
                const x = ((entry.lon + 180) / 360) * 100;
                const y = ((90 - entry.lat) / 180) * 100;
                const color = severityColor(entry.alertSeverity);
                return (
                  <div
                    key={i}
                    className="absolute"
                    style={{
                      left: `${Math.max(5, Math.min(95, x))}%`,
                      top: `${Math.max(5, Math.min(95, y))}%`,
                    }}
                  >
                    <div
                      className="w-2 h-2 rounded-full animate-pulse"
                      style={{
                        background: color,
                        boxShadow: `0 0 8px ${color}, 0 0 16px ${color}40`,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sign-in Locations Table */}
        <div
          className="rounded-lg overflow-hidden"
          style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: `1px solid ${SEC.border}` }}
          >
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" style={{ color: SEC.cyan }} />
              <h3
                className="text-xs font-semibold tracking-[0.15em] uppercase"
                style={{ color: SEC.t1 }}
              >
                Threat Locations
              </h3>
              <span className="text-[10px] font-mono" style={{ color: SEC.t3 }}>
                {sortedEntries.length} entries
              </span>
            </div>
            {lookingUp && (
              <div className="flex items-center gap-1.5 text-xs" style={{ color: SEC.cyan }}>
                <Loader2 className="h-3 w-3 animate-spin" />
                Looking up IPs...
              </div>
            )}
          </div>

          {sortedEntries.length === 0 ? (
            <div className="text-center py-12" style={{ color: SEC.t3 }}>
              <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">
                {loading ? 'Loading...' : 'No IP-based threats detected in recent alerts'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: SEC.panel2, borderBottom: `1px solid ${SEC.border}` }}>
                    <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider cursor-pointer select-none" style={{ color: SEC.t3, fontSize: '10px' }}>
                      IP Address
                    </th>
                    <th
                      className="text-left px-4 py-2.5 font-medium uppercase tracking-wider cursor-pointer select-none"
                      style={{ color: SEC.t3, fontSize: '10px' }}
                      onClick={() => handleSort('city')}
                    >
                      City{sortArrow('city')}
                    </th>
                    <th
                      className="text-left px-4 py-2.5 font-medium uppercase tracking-wider cursor-pointer select-none"
                      style={{ color: SEC.t3, fontSize: '10px' }}
                      onClick={() => handleSort('country')}
                    >
                      Country{sortArrow('country')}
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>
                      ISP / Org
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>
                      Client
                    </th>
                    <th
                      className="text-center px-4 py-2.5 font-medium uppercase tracking-wider cursor-pointer select-none"
                      style={{ color: SEC.t3, fontSize: '10px' }}
                      onClick={() => handleSort('severity')}
                    >
                      Severity{sortArrow('severity')}
                    </th>
                    <th
                      className="text-center px-4 py-2.5 font-medium uppercase tracking-wider cursor-pointer select-none"
                      style={{ color: SEC.t3, fontSize: '10px' }}
                      onClick={() => handleSort('threatScore')}
                    >
                      Threat Score{sortArrow('threatScore')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry, i) => {
                    const sevColor = severityColor(entry.alertSeverity);
                    const threatPct = entry.threatScore || 0;
                    return (
                      <tr
                        key={i}
                        className="transition-colors hover:bg-white/[0.02]"
                        style={{ borderBottom: `1px solid ${SEC.border}` }}
                      >
                        <td className="px-4 py-2.5 font-mono" style={{ color: SEC.t1 }}>
                          {entry.ip}
                        </td>
                        <td className="px-4 py-2.5" style={{ color: SEC.t2 }}>
                          {entry.city}{entry.state ? `, ${entry.state}` : ''}
                        </td>
                        <td className="px-4 py-2.5" style={{ color: SEC.t2 }}>
                          {entry.countryCode && (
                            <span className="mr-1.5">{countryFlag(entry.countryCode)}</span>
                          )}
                          {entry.country}
                        </td>
                        <td className="px-4 py-2.5 truncate max-w-[200px]" style={{ color: SEC.t3 }}>
                          {entry.org || entry.isp || '-'}
                        </td>
                        <td className="px-4 py-2.5" style={{ color: SEC.t2 }}>
                          {entry.clientName}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                            style={{ background: `${sevColor}20`, color: sevColor, border: `1px solid ${sevColor}40` }}
                          >
                            {entry.alertSeverity}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {entry.threatScore != null ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: SEC.border }}>
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${threatPct}%`,
                                    background: threatPct > 75 ? SEC.red : threatPct > 40 ? SEC.amber : SEC.green,
                                  }}
                                />
                              </div>
                              <span className="font-mono text-[10px]" style={{
                                color: threatPct > 75 ? SEC.red : threatPct > 40 ? SEC.amber : SEC.green,
                              }}>
                                {threatPct}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: SEC.t4 }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Convert country code to flag emoji
function countryFlag(code: string): string {
  try {
    return code
      .toUpperCase()
      .split('')
      .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
      .join('');
  } catch {
    return '';
  }
}
